import { ReferenceTableTag } from './reference-table.interface';
import { Entity } from '../../../../domain/entity';
import { validationProvider } from '../../../../infrastructure/validation/validation.provider';
import { ReferenceTableDeletedEvent, } from './event/reference-table-deleted.event';
import { ReferenceTableNameSetEvent, } from './event/reference-table-name-set.event';
import { ReferenceTableDescriptionSetEvent, } from './event/reference-table-description-set.event';
import { ReferenceTableItemDeletedEvent, } from './event/reference-table-item-deleted.event';
import { ReferenceTableInitializedEvent, } from './event/reference-table-initialized.event';
import { ReferenceTableItemAttributesReplacedEvent, } from './event/reference-table-item-attributes-replaced.event';
import { ReferenceTableItemAddedEvent, } from './event/reference-table-item-added.event';
import projections from './projections-index';
import { ReferenceTableItemEntity, } from './reference-table-item.entity';
export class ReferenceTableEntity extends Entity {
    constructor(properties) {
        super(properties.id);
        this._items = new Map();
        this._tenantId = validationProvider.validateAndTransformId(properties.tenantId, {
            name: 'Tenant ID',
            required: true,
        });
        this._name = validationProvider.validateAndTransformString(properties.name, {
            name: 'Name',
            required: true,
        });
        this._description = validationProvider.validateAndTransformString(properties.description, {
            name: 'Description',
        });
        this._tag = validationProvider.validateAndTransformEnumValue(properties.tag, Object.values(ReferenceTableTag), { name: 'ReferenceTable Tag' });
        this._tp2LookupTableName = validationProvider.validateAndTransformString(properties.tp2LookupTableName, {
            name: 'Tp2 Lookup Table Name',
        });
    }
    static initialize(properties, items, metadata) {
        const table = new ReferenceTableEntity(properties);
        const event = table.initialize(items, metadata);
        return { table, event };
    }
    initialize(items, metadata) {
        items.forEach(({ itemId, attributes, tp2LookupTableItemId }) => {
            const item = new ReferenceTableItemEntity({
                id: itemId,
                attributes,
                tp2LookupTableItemId,
            });
            this._items.set(item.id, item);
        });
        return new ReferenceTableInitializedEvent({
            id: this.id,
            tenantId: this.tenantId,
            name: this.name,
            description: this.description,
            tag: this.tag,
            tp2LookupTableName: this._tp2LookupTableName,
            items: Array.from(this._items.values()).map(item => ({
                itemId: item.id,
                attributes: item.attributes,
                tp2LookupTableItemId: item.tp2LookupTableItemId,
            })),
        }, metadata);
    }
    setReferenceTableName(name, metadata) {
        if (name !== undefined) {
            this.name = validationProvider.validateAndTransformString(name, {
                name: 'Name',
                required: true,
            });
        }
        return new ReferenceTableNameSetEvent({
            id: this.id,
            name: this.name,
        }, metadata);
    }
    setReferenceTableDescription(description, metadata) {
        if (description !== undefined) {
            this.description = validationProvider.validateAndTransformString(description, {
                name: 'Description',
            });
        }
        return new ReferenceTableDescriptionSetEvent({
            id: this.id,
            description: this.description,
        }, metadata);
    }
    delete(metadata) {
        return new ReferenceTableDeletedEvent({
            id: this.id,
            deleted: true,
        }, metadata);
    }
    deleteItem(itemId, metadata) {
        const item = this._items.get(itemId);
        if (!item) {
            throw new Error(`Reference table item with id ${itemId} not found`);
        }
        this._items.delete(itemId);
        return new ReferenceTableItemDeletedEvent({
            referenceTableId: this.id,
            itemId,
        }, metadata);
    }
    addItem(itemId, attributes, tp2LookupTableItemId, metadata) {
        if (this._items.has(itemId)) {
            throw new Error(`Reference table item with id ${itemId} already exists`);
        }
        const item = new ReferenceTableItemEntity({
            id: itemId,
            attributes,
            tp2LookupTableItemId,
        });
        this._items.set(item.id, item);
        return new ReferenceTableItemAddedEvent({
            referenceTableId: this.id,
            itemId: item.id,
            attributes: item.attributes,
            tp2LookupTableItemId: item.tp2LookupTableItemId,
        }, metadata);
    }
    replaceItemAttributes(itemId, attributes, tp2LookupTableItemId, metadata) {
        const item = this._items.get(itemId);
        if (!item) {
            throw new Error(`Reference table item with id ${itemId} not found`);
        }
        const previousAttributes = { ...item.attributes };
        item.replaceAttributes(attributes);
        if (tp2LookupTableItemId !== undefined) {
            item.tp2LookupTableItemId = tp2LookupTableItemId ?? undefined;
        }
        return new ReferenceTableItemAttributesReplacedEvent({
            referenceTableId: this.id,
            itemId: item.id,
            previousAttributes,
            attributes: item.attributes,
            tp2LookupTableItemId: item.tp2LookupTableItemId,
        }, metadata);
    }
    static restoreAggregateState(events, snapshot) {
        return super.buildAggregateState(events, projections, snapshot);
    }
    static getFieldFromTag(fields, tag) {
        return fields.find(field => field.tag === tag);
    }
    get tenantId() {
        return this._tenantId;
    }
    get name() {
        return this._name || '';
    }
    set name(name) {
        this._name = name;
    }
    set description(description) {
        this._description = description;
    }
    get description() {
        return this._description || '';
    }
    get tag() {
        return this._tag;
    }
    set tag(tag) {
        this._tag = tag;
    }
    get tp2LookupTableName() {
        return this._tp2LookupTableName;
    }
    get items() {
        return Array.from(this._items.values());
    }
}
//# sourceMappingURL=reference-table.model.js.map